import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  ArrowLeft,
  Copy,
  CheckCircle,
  Gift,
  LogOut,
  Sparkles,
  MessageSquare,
  Share2,
  Mail,
  Users,
  Heart,
  Target,
  Megaphone,
} from "lucide-react";

const ReferralPromoLibrary = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const socialPosts = [
    {
      id: "social-1",
      platform: "General",
      title: "Healthcare Professional Connection",
      content: `🏥 Know a healthcare practice struggling with operations, patient care, or staff burnout?

I've partnered with RNB Consulting Group - they specialize in transforming healthcare practices through proven programs like CareRevive and Foundations First.

If you know anyone who could benefit, use my referral link: [YOUR LINK]

Let's help healthcare practices thrive! 💪`,
    },
    {
      id: "social-2",
      platform: "LinkedIn",
      title: "Professional Network Post",
      content: `I'm excited to share an opportunity for healthcare professionals in my network.

RNB Consulting Group offers transformative programs for medical practices:

✅ CareRevive ($750) - Practice revival & optimization
✅ Foundations First ($1,250) - Operational excellence

If you know a practice owner, administrator, or healthcare leader who wants to improve their operations and patient care, I'd love to connect them.

Use my referral link: [YOUR LINK]

#HealthcareConsulting #PracticeManagement #HealthcareLeadership`,
    },
    {
      id: "social-3",
      platform: "Instagram/Facebook",
      title: "Story/Post Template",
      content: `💼 Healthcare friends! 

Do you know a medical practice that could use some help with:
• Streamlining operations
• Improving patient experience  
• Reducing staff burnout
• Growing revenue

I've partnered with RNB Consulting Group and they're amazing at transforming practices!

🔗 Link in bio or DM me for details!

#HealthcareConsulting #MedicalPractice #HealthcareTransformation`,
    },
  ];

  const emailTemplates = [
    {
      id: "email-1",
      title: "Warm Introduction Email",
      subject: "Thought of You - Healthcare Practice Consulting",
      content: `Hi [Name],

I hope this message finds you well! I wanted to reach out because I recently learned about RNB Consulting Group, and I immediately thought of you.

They specialize in helping healthcare practices optimize their operations, improve patient care, and reduce burnout. Their programs have helped practices across the country transform their businesses.

Their two main programs are:
• CareRevive ($750) - A comprehensive practice revival program
• Foundations First ($1,250) - Focused on building operational excellence

If your practice (or someone you know) could benefit from expert consulting, I'd be happy to share more details. I'm actually a referral partner with them, so if you decide to sign up, just use my referral code: [YOUR CODE]

Would love to catch up soon regardless!

Best,
[Your Name]`,
    },
    {
      id: "email-2",
      title: "Follow-up Email",
      subject: "Quick Follow-up - Healthcare Consulting Opportunity",
      content: `Hi [Name],

Just wanted to follow up on my previous message about RNB Consulting Group.

I know running a practice is incredibly demanding, and finding time to explore new opportunities can be tough. But I genuinely believe their programs could make a real difference.

A few quick highlights:
✓ Proven track record with healthcare practices
✓ Flexible programs starting at $750
✓ Focus on sustainable improvements, not quick fixes

If you're interested, you can learn more at their website or reach out to me directly. My referral code is [YOUR CODE] if you decide to sign up.

No pressure at all - just wanted to make sure this was on your radar!

Talk soon,
[Your Name]`,
    },
  ];

  const talkingPoints = [
    {
      id: "tp-1",
      title: "Why RNB Consulting?",
      points: [
        "Founded by healthcare industry experts with decades of experience",
        "Proven track record of transforming struggling practices into thriving businesses",
        "Customized approach - not one-size-fits-all solutions",
        "Focus on sustainable, long-term improvements",
        "Ongoing support throughout the program",
      ],
    },
    {
      id: "tp-2",
      title: "CareRevive Program Benefits",
      points: [
        "Comprehensive practice assessment and analysis",
        "Customized revival strategy tailored to specific challenges",
        "Hands-on implementation support",
        "Staff training and development resources",
        "Only $750 - exceptional value for comprehensive consulting",
      ],
    },
    {
      id: "tp-3",
      title: "Foundations First Program Benefits",
      points: [
        "Deep dive into operational systems and processes",
        "Build a strong foundation for growth and scalability",
        "Revenue optimization strategies",
        "Patient experience enhancement",
        "Leadership development for practice owners",
        "$1,250 investment with significant ROI potential",
      ],
    },
    {
      id: "tp-4",
      title: "Common Pain Points to Address",
      points: [
        "Staff burnout and high turnover rates",
        "Inefficient scheduling and patient flow",
        "Revenue leakage and billing issues",
        "Poor patient satisfaction scores",
        "Difficulty scaling or growing the practice",
        "Lack of clear systems and processes",
      ],
    },
  ];

  const audienceIdeas = [
    {
      icon: Users,
      title: "Healthcare Professionals in Your Network",
      description: "Doctors, nurses, practice managers, and administrators you know personally or professionally.",
    },
    {
      icon: MessageSquare,
      title: "Professional Groups & Forums",
      description: "LinkedIn groups, Facebook communities, and forums focused on healthcare practice management.",
    },
    {
      icon: Target,
      title: "Local Medical Associations",
      description: "State or regional medical associations, dental societies, or specialty groups.",
    },
    {
      icon: Heart,
      title: "Healthcare Workers You've Helped",
      description: "If you've provided services to healthcare professionals, they may know practices that need help.",
    },
  ];

  return (
    <div className="min-h-screen bg-rnb-light">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-rnb-teal/10 flex items-center justify-center">
                <Gift className="h-5 w-5 text-rnb-teal" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-rnb-navy">
                  Promo Library
                </h1>
                <p className="text-sm text-rnb-gray">Resources to help you share</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-rnb-gray hover:text-red-600"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/partner")}
          className="flex items-center text-rnb-blue hover:text-rnb-navy transition-colors mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </button>

        {/* Intro Card */}
        <Card className="card-modern mb-8 bg-gradient-to-r from-rnb-navy to-rnb-blue text-white">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-white/10">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-bold mb-2">
                  Your Referral Toolkit
                </h2>
                <p className="text-white/80">
                  Use these templates, talking points, and ideas to effectively share our programs 
                  with your network. Remember to replace [YOUR CODE] and [YOUR LINK] with your 
                  actual referral code and link!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="social" className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="social" className="gap-2">
              <Share2 className="h-4 w-4" />
              <span className="hidden sm:inline">Social Posts</span>
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Email Templates</span>
            </TabsTrigger>
            <TabsTrigger value="talking" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Talking Points</span>
            </TabsTrigger>
            <TabsTrigger value="audience" className="gap-2">
              <Megaphone className="h-4 w-4" />
              <span className="hidden sm:inline">Audience Ideas</span>
            </TabsTrigger>
          </TabsList>

          {/* Social Posts Tab */}
          <TabsContent value="social" className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                Ready-to-Use Social Media Posts
              </h3>
              <p className="text-rnb-gray">
                Copy, customize, and share on your favorite platforms
              </p>
            </div>
            {socialPosts.map((post) => (
              <Card key={post.id} className="card-modern">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{post.title}</CardTitle>
                      <CardDescription>Best for: {post.platform}</CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(post.content, post.id)}
                      data-testid={`copy-${post.id}`}
                    >
                      {copiedId === post.id ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="whitespace-pre-wrap text-sm bg-rnb-light p-4 rounded-lg font-sans">
                    {post.content}
                  </pre>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email" className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                Email Templates
              </h3>
              <p className="text-rnb-gray">
                Professional emails to send to your contacts
              </p>
            </div>
            {emailTemplates.map((email) => (
              <Card key={email.id} className="card-modern">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{email.title}</CardTitle>
                      <CardDescription>Subject: {email.subject}</CardDescription>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(`Subject: ${email.subject}\n\n${email.content}`, email.id)}
                      data-testid={`copy-${email.id}`}
                    >
                      {copiedId === email.id ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="whitespace-pre-wrap text-sm bg-rnb-light p-4 rounded-lg font-sans">
                    {email.content}
                  </pre>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* Talking Points Tab */}
          <TabsContent value="talking" className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                Key Talking Points
              </h3>
              <p className="text-rnb-gray">
                Use these points when discussing the programs with potential referrals
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {talkingPoints.map((tp) => (
                <Card key={tp.id} className="card-modern">
                  <CardHeader>
                    <CardTitle className="text-lg">{tp.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {tp.points.map((point, index) => (
                        <li key={point.title || point.name || `point-${index}`} className="flex items-start gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-rnb-teal mt-0.5 flex-shrink-0" />
                          <span className="text-rnb-gray">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Audience Ideas Tab */}
          <TabsContent value="audience" className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="font-heading text-xl font-semibold text-rnb-navy mb-2">
                Who to Share With
              </h3>
              <p className="text-rnb-gray">
                Ideas for finding people who might benefit from our programs
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {audienceIdeas.map((idea, index) => (
                <Card key={idea.title || idea.name || `idea-${index}`} className="card-modern">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-rnb-teal/10">
                        <idea.icon className="h-6 w-6 text-rnb-teal" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-rnb-navy mb-1">{idea.title}</h4>
                        <p className="text-sm text-rnb-gray">{idea.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Tips Card */}
            <Card className="card-modern bg-gradient-to-br from-rnb-teal/5 to-rnb-blue/5">
              <CardContent className="p-6">
                <h4 className="font-heading font-semibold text-rnb-navy mb-4">
                  Pro Tips for Success
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm">
                    <span className="font-bold text-rnb-teal">1.</span>
                    <span className="text-rnb-gray">
                      <strong>Be genuine</strong> - Share from personal experience or genuine belief in the programs
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <span className="font-bold text-rnb-teal">2.</span>
                    <span className="text-rnb-gray">
                      <strong>Listen first</strong> - Understand their challenges before suggesting solutions
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <span className="font-bold text-rnb-teal">3.</span>
                    <span className="text-rnb-gray">
                      <strong>Follow up</strong> - A gentle follow-up can make all the difference
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-sm">
                    <span className="font-bold text-rnb-teal">4.</span>
                    <span className="text-rnb-gray">
                      <strong>Be patient</strong> - Decision-makers in healthcare often need time to evaluate options
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReferralPromoLibrary;
